import React, { useState } from 'react';
import { ShieldCheckIcon, DocumentTextIcon, ArrowRightOnRectangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { userAPI } from '../../services/api';
import { useAppDispatch } from '../../hooks/redux';
import { updateUser, logout } from '../../store/slices/authSlice';
import toast from 'react-hot-toast';

interface TermsAcceptanceModalProps {
  isOpen: boolean;
}

const TermsAcceptanceModal: React.FC<TermsAcceptanceModalProps> = ({ isOpen }) => {
  const dispatch = useAppDispatch();
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleAccept = async () => {
    if (!agreed) {
      toast.error('Please check the box to confirm that you agree to the terms.');
      return;
    }

    setSubmitting(true);
    try {
      await userAPI.acceptTerms();
      dispatch(updateUser({ agreed_terms: true, agreed_terms_at: new Date().toISOString() }));
      toast.success('Thank you! Terms & Privacy Policy accepted.');
    } catch (err: any) {
      console.error('Failed to accept terms:', err);
      // Fallback: If backend returns success or error, still update local state if response was ok
      toast.error(err?.response?.data?.error || 'Failed to record terms acceptance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeclineAndLogout = () => {
    dispatch(logout());
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200 select-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-modal-title"
    >
      <div className="bg-white dark:bg-gray-900 rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-gray-100 dark:border-gray-800 relative">
        {/* Header */}
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-100 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 flex-shrink-0">
            <ShieldCheckIcon className="h-7 w-7" />
          </div>
          <div>
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-400">
              Mandatory Platform Update
            </span>
            <h3 id="terms-modal-title" className="text-xl font-black text-gray-900 dark:text-white tracking-tight mt-0.5">
              Terms &amp; Privacy Policy
            </h3>
          </div>
        </div>

        <p className="text-xs text-gray-600 dark:text-gray-300 font-medium leading-relaxed mb-4">
          ClickCollab has updated its Terms of Service and Privacy Policy. To keep our creative community safe, professional, and compliant, all members must review and accept these core terms to continue.
        </p>

        {/* Highlighted Terms Summary Box */}
        <div className="bg-gray-50 dark:bg-white/5 rounded-2xl p-4 border border-gray-200/70 dark:border-gray-800 space-y-3 mb-5 max-h-56 overflow-y-auto">
          <div className="flex items-start space-x-2.5 text-xs">
            <span className="text-base leading-none mt-0.5">⚖️</span>
            <div>
              <strong className="text-gray-900 dark:text-white block font-bold">Platform Authority &amp; Account Control</strong>
              <span className="text-gray-600 dark:text-gray-400 leading-relaxed text-[11px]">
                ClickCollab holds complete authority to temporarily disable or permanently delete any user account or content in all cases where platform rules, community standards, or service integrity are breached.
              </span>
            </div>
          </div>

          <div className="flex items-start space-x-2.5 text-xs">
            <span className="text-base leading-none mt-0.5">🚫</span>
            <div>
              <strong className="text-gray-900 dark:text-white block font-bold">Strict Prohibition of Nudity &amp; Adult Content</strong>
              <span className="text-gray-600 dark:text-gray-400 leading-relaxed text-[11px]">
                We maintain a strict zero-tolerance policy against any form of nudity, sexually explicit material, or adult content across portfolios, messages, projects, profile photos, and collaboration requests.
              </span>
            </div>
          </div>

          <div className="flex items-start space-x-2.5 text-xs">
            <span className="text-base leading-none mt-0.5">📍</span>
            <div>
              <strong className="text-gray-900 dark:text-white block font-bold">Regional Operational Focus</strong>
              <span className="text-gray-600 dark:text-gray-400 leading-relaxed text-[11px]">
                Platform operations and on-demand creator collaborations are currently focused within Bengaluru and a 100km radius.
              </span>
            </div>
          </div>
        </div>

        {/* Link to Full Document */}
        <div className="mb-5 flex items-center justify-between text-xs">
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 dark:text-indigo-400 hover:underline font-bold flex items-center space-x-1.5"
          >
            <DocumentTextIcon className="h-4 w-4" />
            <span>Read Full Privacy Policy &amp; Terms of Service</span>
            <span className="text-[10px] text-gray-400">(opens in new tab)</span>
          </a>
        </div>

        {/* Agreement Checkbox */}
        <div className="mb-6 p-3 bg-indigo-50/60 dark:bg-indigo-950/20 rounded-2xl border border-indigo-100 dark:border-indigo-900/40 flex items-start space-x-3">
          <input
            type="checkbox"
            id="terms-acceptance-checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600 flex-shrink-0"
          />
          <label
            htmlFor="terms-acceptance-checkbox"
            className="text-xs text-gray-700 dark:text-gray-200 font-medium leading-relaxed cursor-pointer select-none"
          >
            I have read, understood, and agree to ClickCollab's{' '}
            <strong className="text-gray-900 dark:text-white">Privacy Policy &amp; Terms of Service</strong>, including platform account authority and content rules.
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between gap-3 pt-1">
          <button
            type="button"
            onClick={handleDeclineAndLogout}
            className="px-4 py-2.5 rounded-xl text-xs font-bold text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors uppercase tracking-wider flex items-center space-x-1.5"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4" />
            <span>Decline &amp; Sign Out</span>
          </button>

          <button
            type="button"
            onClick={handleAccept}
            disabled={!agreed || submitting}
            className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl text-xs font-black uppercase tracking-wider hover:opacity-90 transition-all shadow-lg shadow-indigo-500/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <CheckCircleIcon className="h-4 w-4" />
            <span>{submitting ? 'Recording...' : 'Accept & Continue'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsAcceptanceModal;
